/**
 * Google Tag Manager Configuration & Helpers
 * Provides type-safe dataLayer event tracking (shared pattern with marketing site)
 */

// GTM Container ID (same as marketing site)
export const GTM_ID = 'GTM-WCCLDV6S';

// Consent storage key (shared with marketing site)
export const CONSENT_KEY = 'cookie_consent';

// Consent status types
export type ConsentStatus = 'granted' | 'denied' | 'pending';

// Extend Window interface for dataLayer and gtag
declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
    gtag?: (...args: unknown[]) => void;
  }
}

// DataLayer event types
export interface PageViewEvent {
  event: 'page_view';
  page_path: string;
  page_title: string;
  page_location?: string;
  page_referrer?: string;
}

export interface FormSubmitEvent {
  event: 'form_submit';
  form_name: string;
  form_data?: Record<string, unknown>;
}

export interface CTAClickEvent {
  event: 'cta_click';
  cta_name: string;
  cta_destination: string;
}

export interface RegistrationEvent {
  event: 'sign_up';
  method: 'email' | 'google' | 'microsoft' | 'apple';
  user_role?: 'provider' | 'substitute';
}

export interface ConsentUpdateEvent {
  event: 'consent_update';
  consent_status: ConsentStatus;
}

export interface CustomEvent {
  event: string;
  [key: string]: unknown;
}

export type DataLayerEvent = PageViewEvent | FormSubmitEvent | CTAClickEvent | RegistrationEvent | ConsentUpdateEvent | CustomEvent;

/**
 * Push an event to the dataLayer
 * Safe to call even if GTM is not loaded
 */
export const pushEvent = (event: DataLayerEvent): void => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
};

/**
 * Get consent status from localStorage
 */
export const getConsentStatus = (): ConsentStatus => {
  if (typeof window === 'undefined') return 'pending';

  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'granted' || stored === 'denied') {
    return stored;
  }
  return 'pending';
};

/**
 * Check if tracking is allowed (consent granted)
 */
export const hasConsent = (): boolean => {
  return getConsentStatus() === 'granted';
};

/**
 * Update GTM consent state
 * Called when user accepts/rejects cookies
 */
export const updateGTMConsent = (granted: boolean): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const consentState = granted ? 'granted' : 'denied';

  window.gtag('consent', 'update', {
    ad_storage: consentState,
    ad_user_data: consentState,
    ad_personalization: consentState,
    analytics_storage: consentState,
  });

  // Also push consent update event to dataLayer
  pushEvent({
    event: 'consent_update',
    consent_status: consentState,
  });
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title: string, pageLocation?: string, pageReferrer?: string): void => {
  if (!hasConsent()) return;

  pushEvent({
    event: 'page_view',
    page_path: path,
    page_title: title,
    ...(pageLocation && { page_location: pageLocation }),
    ...(pageReferrer && { page_referrer: pageReferrer }),
  });
};

/**
 * Track a form submission
 */
export const trackFormSubmit = (formName: string, formData?: Record<string, unknown>): void => {
  if (!hasConsent()) return;

  pushEvent({
    event: 'form_submit',
    form_name: formName,
    form_data: formData,
  });
};

/**
 * Track a CTA click
 */
export const trackCTAClick = (ctaName: string, destination: string): void => {
  if (!hasConsent()) return;

  pushEvent({
    event: 'cta_click',
    cta_name: ctaName,
    cta_destination: destination,
  });
};

/**
 * Track registration completion
 * Pushes sign_up event to dataLayer for GTM to handle
 */
export const trackRegistrationConversion = (method: 'email' | 'google' | 'microsoft' | 'apple' = 'email', userRole?: 'provider' | 'substitute'): void => {
  if (!hasConsent()) {
    console.debug('[GTM] Skipping registration tracking - no consent');
    return;
  }

  pushEvent({
    event: 'sign_up',
    method,
    user_role: userRole,
  });

  console.debug('[GTM] Registration tracked:', { method, userRole });
};

/**
 * Track a custom event
 */
export const trackCustomEvent = (eventName: string, eventData?: Record<string, unknown>): void => {
  if (!hasConsent()) return;

  pushEvent({
    event: eventName,
    ...eventData,
  });
};
